import { usePathname } from 'next/navigation';
import { useStore } from 'zustand';
import { useEffect, useRef } from 'react';

import { resetUserLocation, setUserLocation, userLocationStore } from '@/store/user-location.store';
import { getUserLocationStorage, removeUserLocationStorage } from '@/storage/location.storage';
import { useSearchParams } from '@/hooks/use-search-params.hook';
import { PersistedUserNavigationSearchParams } from '@/types/enums/persisted-user-navigation-search-params.enum';
import { slugify } from '@/utils/formatters/slugfy';
import { Place } from '@/types/google-maps.types';

interface UseHandleLocationParams {
  city?: string;
  state?: string;
}

export const useHandleLocation = ({ city, state }: UseHandleLocationParams) => {
  const {
    cidade: currentCity, removeParams, setParams,
  } = useSearchParams([...Object.values(PersistedUserNavigationSearchParams)]);

  const userLocation = useStore(userLocationStore, (state) => state.userLocation);
  const isHome = usePathname() === '/';
  const hasClearedOnHomeRef = useRef(false);

  useEffect(() => {
    if (isHome) {
      if (hasClearedOnHomeRef.current) {
        return;
      }

      hasClearedOnHomeRef.current = true;

      if (currentCity) {
        removeParams(PersistedUserNavigationSearchParams.CIDADE);
      }

      resetUserLocation();
      removeUserLocationStorage();

      return;
    }

    hasClearedOnHomeRef.current = false;

    function handleUpdateLocation(newLocation: Place) {
      const newSlug = slugify(`${newLocation?.city}-${newLocation?.state}`);
      setUserLocation(newLocation);
      setParams({
        [PersistedUserNavigationSearchParams.CIDADE]: newSlug,
      });
    }

    if (city && state && !userLocation) {
      const storageLocation = getUserLocationStorage();
      const hasValidStorage
        = slugify(`${storageLocation?.city}-${storageLocation?.state}`) === slugify(`${city}-${state}`);

      if (storageLocation && hasValidStorage) {
        handleUpdateLocation(storageLocation);
        return;
      }
    }

    if (city && state) {
      const userPlace = slugify(`${userLocation?.city}-${userLocation?.state}`);
      const citySlug = slugify(`${city}-${state}`);
      const hasChanged = userPlace !== citySlug;

      if (hasChanged) {
        handleUpdateLocation({
          formattedAddress: '',
          types: [],
          placeId: '',
          city,
          state,
        });
      }
    }
  }, [city, currentCity, isHome, removeParams, setParams, state, userLocation]);
};
